import { defineComponent as _defineComponent } from 'vue'

import type { MetaStoreType } from '@/stores/pinia/useMetaStore';

const __default__ = {
	name: 'App',
	metaInfo() {
		return (this as unknown as { metaStore: MetaStoreType }).metaStore.head;
	},
};

import Vue, { getCurrentInstance, nextTick, onMounted, watch } from 'vue';

// @ts-expect-error Could not find a declaration file for module 'vue-lazy-hydration'.
import LazyHydrate from 'vue-lazy-hydration';
// @ts-expect-error Could not find a declaration file for module 'vue-no-ssr'.
import NoSSR from 'vue-no-ssr';

import { useRoute, useRouter } from 'vue-router/composables';

import { Platform } from '@/@types/graphql-types';
import ContentSSR from '@/components/ContentSSR.vue';
import AppFooter from '@/components/footer/AppFooter.vue';
import AppHeader from '@/components/nav/AppHeader.vue';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';

import StyleInjector from '@/components/StyleInjector';

import { useLoginFlow } from '@/features/auth/composables/useLoginFlow';

import {
	RegisterSessionStartDocument,
	RegisterSessionStartMutation,
	RegisterSessionStartMutationVariables,
} from '@/graphql/mutation/RegisterSessionStart.mutation';

import { useMutation } from '@/helpers/composables/useApollo';
import { enableScroll } from '@/helpers/scroll-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getVm } from '@/helpers/vm-helper';

import { useExperimentInitialization } from '@/libraries/experiment';

import { initLanguage } from '@/main.ssr';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

import CanaryMarker from '@/ui-components/CanaryMarker.vue';

import '@/styles/global.scss';
import '@fortawesome/fontawesome-svg-core/styles.css';

// make NoSSR a globaly available component

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

Vue.component('NoSSR', NoSSR);
Vue.component('LazyHydrate', LazyHydrate);

// AppFooter global to avoid CLS
Vue.component('AppFooter', AppFooter);

// Components should be multiple words error
// eslint-disable-next-line
Vue.component('Content', ContentSSR);

Vue.component('ClickoutAnchor', ClickoutAnchor);

const LowPriorityComponents = () =>
	import(/* webpackChunkName: "low-priority-components" */ '@/components/LowPriorityComponents.vue');

if (process.client) {
	import('@/helpers/tracking/cwv-helper').then(({ initCoreWebVitalsMonitor }) => initCoreWebVitalsMonitor({}));
}

const metaStore = useMetaStore();
const routingStore = useRoutingStore();
const languageStore = useLanguageStore();
const userStore = useUserStore();

const route = useRoute();
const router = useRouter();
const { initializeExperiments } = useExperimentInitialization();

const { initLoginFlow } = useLoginFlow();

const { mutate: registerSessionStart, onError } = useMutation<
	RegisterSessionStartMutation,
	RegisterSessionStartMutationVariables
>(RegisterSessionStartDocument);

onError(error => {
	captureMessageForSentry('SSR App: Unable to fire Register Session Start Mutation', {
		where: '[SSRApp.vue] onMounted()',
		error,
	});
});

onMounted(async () => {
	const { proxy: instance } = getCurrentInstance() ?? { proxy: getVm() };

	watch(
		() => languageStore.webLocale,
		async (webLocale, oldWebLocale) => {
			if (webLocale === oldWebLocale) return;
			await router.changeLocale(webLocale);
			await initLanguage({ ...instance.$context, webLocale });
		},
		{ immediate: false }
	);

	await initLoginFlow();

	nextTick(() => {
		const int = setInterval(() => {
			// remove loader
			const loader = document.querySelector('#loader');
			loader?.parentNode?.removeChild(loader);
			clearInterval(int);
		}, 50);

		// reactivate scroll on page rendered
		enableScroll();
	});

	const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	// this causes interaction lag on safari browsers
	// userStore.setTimeZone(browserTimeZone);

	const registerMutation = await registerSessionStart({
		input: {
			platform: Platform.Web,
			language: languageStore.language,
			country: languageStore.country,
			timezone: browserTimeZone,
		},
	});

	const experiments = registerMutation?.data?.registerSessionStart.experiments ?? [];
	initializeExperiments(
		experiments.map(el => ({
			countries: el.countries,
			languages: el.languages,
			name: el.name,
			sample: el.sample,
			variant: el.variant ?? null,
		}))
	);

	await TrackingHelper.isInitializedPromise;

	//need to initialize PASubgenres for it to populate trackers as SEO user
	TrackingHelper.refreshPermanentAudiencesSubgenres([]);
	TrackingHelper.refreshPermanentAudiences(userStore.permanentAudiences);

	// route.fullPath is for Home as first page, which has incomplete urlMetadata
	if ((routingStore.urlMetadata?.fullPath ?? route.fullPath) == null) return;

	TrackingHelper.trackAudienceRefresh(userStore.permanentAudiences);
});

expose({ metaStore });

return { __sfc: true,LowPriorityComponents, metaStore, routingStore, languageStore, userStore, route, router, initializeExperiments, initLoginFlow, registerSessionStart, onError, AppHeader, StyleInjector, CanaryMarker }
}

})